import { useState } from 'react';
import type { Call, Lead } from '@marnic/shared';
import { getCounterpartyNumber } from '@marnic/shared';
import { api } from '../lib/api';
import { extractApiErrorMessage } from '../lib/api-error';
import { softphoneService } from '../softphone/service';
import { useSoftphone } from '../store/softphone';

export interface RedialResult {
  redial: (call: Pick<Call, 'direction' | 'fromNumber' | 'toNumber' | 'leadId' | 'campaignId' | 'callbackId'>) => Promise<void>;
  busy: boolean;
  error: string | null;
  canRedial: boolean;
}

export function useRedial(): RedialResult {
  const status = useSoftphone((s) => s.status);
  const setLead = useSoftphone((s) => s.setLead);
  const selectedCallerId = useSoftphone((s) => s.selectedCallerId);
  const selectedCallerProvider = useSoftphone((s) => s.selectedCallerProvider);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canRedial = status !== 'on_call' && status !== 'connecting';

  const redial: RedialResult['redial'] = async (call) => {
    if (!canRedial) {
      setError('Finish the active call before redialling');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const toRaw = getCounterpartyNumber(call);
      const failureContext = `Could not place call [redial-debug selected=${selectedCallerId || 'auto'} provider=${selectedCallerProvider ?? 'auto'} to=${toRaw}]`;
      let leadId = call.leadId ?? undefined;
      if (!leadId) {
        const lookup = await api.post<{ found: boolean; lead: Lead | null }>('/leads/lookup', { phone: toRaw });
        if (lookup.lead) {
          setLead(lookup.lead);
          leadId = lookup.lead.id;
        }
      }
      const result = await softphoneService.dial({
        toRaw,
        leadId,
        campaignId: call.campaignId ?? undefined,
        callbackId: call.callbackId ?? undefined,
        callerId: selectedCallerId || undefined,
        callerProvider: selectedCallerProvider ?? undefined,
      });
      if (!result.ok) {
        setError(result.detail ?? result.reason ?? failureContext);
      }
    } catch (err) {
      setError(extractApiErrorMessage(err, 'Could not place call [redial-debug]'));
    } finally {
      setBusy(false);
    }
  };

  return { redial, busy, error, canRedial };
}
